import { useEffect, useState } from 'react';
import {
  Autocomplete,
  TextField,
  Box,
  Typography,
  CircularProgress,
} from '@mui/material';
import { createFilterOptions } from '@mui/material/Autocomplete';
import { apiClient } from '@/lib/apiClient';
import { FieldLabel } from './FieldLabel';
import { DimensionPrecedenceHint } from './DimensionPrecedenceHint';

/**
 * DimensionValuesEditor — the framework-agnostic core of the inline dimension-values editor
 * (Master-Data-Dimensions spec, Part 2). Lists the dimensions applicable to the host module
 * (active AND not omitted, ordered by sortOrder) and lets the user pick a value per row; the
 * caller receives the working {definitionId → valueId} map via onChange and resolves it to a
 * DimensionSet id on save (resolve-on-save).
 *
 * Used by the JSONForms renderer (DimensionSetEditor) and directly by hand-written panels
 * (the org tree's Dimensions tab).
 *
 * Inherited defaults: when `inheritFromNodeId` is set (a tree node — typically the record's
 * parent), the effective defaults of that node's ancestor chain are fetched and shown grayed,
 * with their source node, for every axis the record itself leaves unset. `inheritSource` names
 * which tree the node belongs to (org unit or product group) and picks the backend endpoint.
 * Display only — an inherited value is never written into the record's own set.
 */

export interface ActiveDimensionValue {
  id: string;
  valueCode: string;
  name: string;
}

export interface ActiveDimension {
  id: string;
  code: string;
  name: string;
  shortName?: string;
  sortOrder: number;
  isMandatory: boolean;
  allowManualOverride: boolean;
  values: ActiveDimensionValue[];
}

interface DimensionSetLineLite {
  dimensionDefinitionId: string;
  dimensionValueId: string;
  valueCodeSnapshot: string;
  valueNameSnapshot: string;
}

/** Mirror of EffectiveDimensionDefaultDto. */
export interface EffectiveDimensionDefault {
  dimensionDefinitionId: string;
  definitionCode: string;
  definitionName: string;
  sortOrder: number;
  dimensionValueId: string;
  valueCode: string;
  valueName: string;
  sourceNodeId: string;
  sourceNodeCode: string;
  sourceNodeName: string;
  isSelf: boolean;
}

/**
 * The trees that supply inherited defaults, mapped to their effective-defaults endpoint. An
 * explicit map (not URL convention) so an unsupported source simply skips the fetch — the
 * inheritance display is best-effort and must never block editing.
 */
export type InheritSource = 'org-unit' | 'product-group';
const EFFECTIVE_DEFAULTS_ENDPOINT: Record<InheritSource, string> = {
  'org-unit': '/api/finance/dimension-set/effective-org-unit-defaults',
  'product-group': '/api/finance/dimension-set/effective-product-group-defaults',
};

export interface DimensionValuesEditorProps {
  /** Module whose active dimensions the editor offers (e.g. 'core', 'sales'). */
  module: string;
  /** Existing DimensionSet to pre-fill from (edit mode). */
  dimensionSetId?: string | null;
  /** Tree node whose ancestor chain supplies inherited defaults (typically the record's parent). */
  inheritFromNodeId?: string | null;
  /** Which tree `inheritFromNodeId` belongs to. Defaults to 'org-unit'. */
  inheritSource?: InheritSource;
  /**
   * The DSL entity this editor is embedded on (e.g. 'TradeType'). When the record occupies a rung
   * of the plan-derivation precedence ladder, the editor states where its defaults rank. Omitted =
   * no hint.
   */
  entity?: string;
  disabled?: boolean;
  label?: string;
  description?: string;
  /**
   * Fired on every user change with the full working {definitionId → valueId} map (pre-filled
   * axes included). NOT fired for the pre-fill itself, so an untouched editor never triggers a
   * re-resolve on save.
   */
  onChange?: (map: Record<string, string | null>) => void;
}

const valueFilter = createFilterOptions<ActiveDimensionValue>({
  // Search a dimension's values by code OR name (client-side; the active-dimensions
  // endpoint already scoped them to this dimension + isActive).
  stringify: (option) => `${option.valueCode} ${option.name}`,
});

export function DimensionValuesEditor({
  module,
  dimensionSetId,
  inheritFromNodeId,
  inheritSource = 'org-unit',
  entity,
  disabled,
  label,
  description,
  onChange,
}: DimensionValuesEditorProps) {
  const [dimensions, setDimensions] = useState<ActiveDimension[]>([]);
  // Working map: dimensionDefinitionId → dimensionValueId (or null when cleared).
  const [selected, setSelected] = useState<Record<string, string | null>>({});
  // Snapshot fallbacks for pre-filled values that are no longer in the active list.
  const [extraValues, setExtraValues] = useState<Record<string, ActiveDimensionValue>>({});
  // Effective inherited defaults keyed by definition id (empty when inheritance is off).
  const [inherited, setInherited] = useState<Record<string, EffectiveDimensionDefault>>({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  // 1. Enumerate the dimensions applicable to the host module.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    apiClient
      .get('/api/finance/dimension-definition/active', { params: { module } })
      .then((res) => {
        if (!cancelled) setDimensions(res.data || []);
      })
      .catch(() => {
        if (!cancelled) setLoadError('Failed to load dimensions');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [module]);

  // 2. Pre-fill from the current DimensionSet's lines when editing an existing record.
  useEffect(() => {
    let cancelled = false;
    if (!dimensionSetId) {
      setSelected({});
      setExtraValues({});
      return;
    }
    apiClient
      .get(`/api/finance/dimension-set/${dimensionSetId}/lines`)
      .then((res) => {
        if (cancelled) return;
        const lines: DimensionSetLineLite[] = res.data || [];
        const map: Record<string, string | null> = {};
        const extras: Record<string, ActiveDimensionValue> = {};
        for (const line of lines) {
          map[line.dimensionDefinitionId] = line.dimensionValueId;
          // Keep a snapshot fallback so inactive pre-filled values still display.
          extras[line.dimensionValueId] = {
            id: line.dimensionValueId,
            valueCode: line.valueCodeSnapshot,
            name: line.valueNameSnapshot,
          };
        }
        setSelected(map);
        setExtraValues(extras);
      })
      .catch(() => {
        /* a missing/empty set just means no pre-fill */
      });
    return () => {
      cancelled = true;
    };
  }, [dimensionSetId]);

  // 3. Inherited defaults from the parent chain — display only, never written to the record.
  useEffect(() => {
    let cancelled = false;
    const endpoint = EFFECTIVE_DEFAULTS_ENDPOINT[inheritSource];
    if (!inheritFromNodeId || !endpoint) {
      setInherited({});
      return;
    }
    apiClient
      .get(`${endpoint}/${inheritFromNodeId}`)
      .then((res) => {
        if (cancelled) return;
        const map: Record<string, EffectiveDimensionDefault> = {};
        for (const d of (res.data || []) as EffectiveDimensionDefault[]) {
          map[d.dimensionDefinitionId] = d;
        }
        setInherited(map);
      })
      .catch(() => {
        if (!cancelled) setInherited({}); // inheritance display is best-effort, never blocks editing
      });
    return () => {
      cancelled = true;
    };
  }, [inheritFromNodeId, inheritSource]);

  const handleSelect = (definitionId: string, value: ActiveDimensionValue | null) => {
    const next = { ...selected, [definitionId]: value ? value.id : null };
    setSelected(next);
    if (value) {
      setExtraValues((prev) => ({ ...prev, [value.id]: value }));
    }
    onChange?.(next);
  };

  // Resolve a definition's options, folding in any snapshot fallback for the selected value.
  const optionsFor = (dim: ActiveDimension): ActiveDimensionValue[] => {
    const selectedId = selected[dim.id];
    if (selectedId && !dim.values.some((v) => v.id === selectedId) && extraValues[selectedId]) {
      return [...dim.values, extraValues[selectedId]];
    }
    return dim.values;
  };

  const resolvedValue = (dim: ActiveDimension): ActiveDimensionValue | null => {
    const selectedId = selected[dim.id];
    if (!selectedId) return null;
    return dim.values.find((v) => v.id === selectedId) || extraValues[selectedId] || null;
  };

  return (
    <Box sx={{ width: '100%' }}>
      <FieldLabel label={label || 'Dimensions'} description={description} />

      {loading && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
          <CircularProgress size={16} />
          <Typography variant="caption" color="text.secondary">
            Loading dimensions…
          </Typography>
        </Box>
      )}

      {loadError && (
        <Typography variant="caption" color="error">
          {loadError}
        </Typography>
      )}

      {!loading && !loadError && dimensions.length === 0 && (
        <Typography variant="caption" color="text.secondary">
          No dimensions configured for this record.
        </Typography>
      )}

      {!loading && !loadError && dimensions.map((dim) => {
        const value = resolvedValue(dim);
        const inheritedDefault = !value ? inherited[dim.id] : undefined;
        return (
          <Box
            key={dim.id}
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'nowrap',
              gap: 1.5,
              py: 0.5,
            }}
          >
            <Typography
              variant="body2"
              sx={{ minWidth: 140, flexShrink: 0, color: 'text.secondary' }}
              title={dim.code}
            >
              {dim.name}
              {dim.isMandatory ? ' *' : ''}
            </Typography>

            <Autocomplete<ActiveDimensionValue>
              sx={{ flex: '0 1 140px', minWidth: 110, maxWidth: 180 }}
              size="small"
              disabled={disabled}
              options={optionsFor(dim)}
              value={value}
              onChange={(_e, newValue) => handleSelect(dim.id, newValue)}
              filterOptions={valueFilter}
              getOptionLabel={(option) => option.valueCode || ''}
              isOptionEqualToValue={(option, val) => option.id === val.id}
              renderOption={(liProps, option) => (
                <li {...liProps} key={option.id}>
                  <Box>
                    <Typography variant="body2">{option.valueCode}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {option.name}
                    </Typography>
                  </Box>
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="standard"
                  placeholder={inheritedDefault ? inheritedDefault.valueCode : ''}
                />
              )}
            />

            {inheritedDefault ? (
              <Typography
                variant="body2"
                data-testid={`inherited-${dim.code}`}
                sx={{
                  flex: '1 1 auto',
                  minWidth: 0,
                  color: 'text.disabled',
                  fontStyle: 'italic',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
                title={`${inheritedDefault.valueCode} – ${inheritedDefault.valueName} · inherited from ${inheritedDefault.sourceNodeName} (${inheritedDefault.sourceNodeCode})`}
              >
                {inheritedDefault.valueName} · inherited from {inheritedDefault.sourceNodeName}
              </Typography>
            ) : (
              <Typography
                variant="body2"
                sx={{
                  flex: '1 1 auto',
                  minWidth: 0,
                  color: 'text.primary',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
                title={value?.name || ''}
              >
                {value?.name || ''}
              </Typography>
            )}
          </Box>
        );
      })}

      {/* Where these defaults rank when a plan line is derived — stated here, at the point of
          entry, rather than only on the explain dialog of a finished line. */}
      {!loading && !loadError && dimensions.length > 0 && <DimensionPrecedenceHint entity={entity} />}
    </Box>
  );
}
